// Client-side support for the "Ask Sam" terminal:
//   · daily question budget (localStorage, resets each day)
//   · greeting detection (so a typed "hi" never costs a question)
//   · counter copy + all user-facing copy in one place
//
// Pure logic — no React. Unit-tested in askSam.test.js.

const STORAGE_KEY = "ask-sam.usage";
export const MAX_QUESTIONS_PER_DAY = 5;

// ─── Date ─────────────────────────────────────────────────────────────────────
// Local YYYY-MM-DD — the budget resets at the visitor's local midnight.
export function todayStamp(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function newId() {
  return "term_" + Math.random().toString(36).slice(2, 12);
}

function freshUsage() {
  return { date: todayStamp(), count: 0, nudged: false, sid: newId() };
}

// ─── Usage object: { date, count, nudged, sid } ───────────────────────────────
export function saveUsage(u) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  } catch {
    /* storage unavailable (private mode) — fail open, the widget still works */
  }
  return u;
}

export function getUsage() {
  let u = null;
  try {
    u = JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    u = null;
  }
  if (!u || typeof u !== "object") u = freshUsage();

  // Daily reset — a new day zeroes the count but keeps sid + nudged.
  if (u.date !== todayStamp()) {
    u = { ...u, date: todayStamp(), count: 0 };
  }
  // Backfill any missing fields (older or partial objects).
  if (typeof u.count !== "number") u.count = 0;
  if (typeof u.nudged !== "boolean") u.nudged = false;
  if (!u.sid) u.sid = newId();

  return saveUsage(u);
}

export function recordQuestion() {
  const u = getUsage();
  u.count += 1;
  return saveUsage(u);
}

export function markNudged() {
  const u = getUsage();
  u.nudged = true;
  return saveUsage(u);
}

export function remaining(u = getUsage()) {
  return Math.max(0, MAX_QUESTIONS_PER_DAY - u.count);
}

// ─── Counter copy ─────────────────────────────────────────────────────────────
export function counterLabel(left) {
  if (left <= 0) return "no questions left today";
  if (left === 1) return "last one today";
  return `${left} left today`;
}

// ─── Greeting detection ───────────────────────────────────────────────────────
// A message that is ONLY a greeting (every word is a greeting/filler word) is
// answered for free. A greeting plus a real question is not caught.
const GREETING_WORDS = new Set([
  "hi", "hii", "hiii", "hiya", "hello", "helo", "hey", "heyy", "heya",
  "yo", "yoo", "howdy", "sup", "greetings", "hola", "morning", "afternoon",
  "evening", "good", "day", "there", "sam", "mate", "everyone", "folks",
]);

export function isPureGreeting(text) {
  if (!text) return false;
  const words = text
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0 || words.length > 4) return false;
  return words.every((w) => GREETING_WORDS.has(w));
}

// ─── Low-effort input ─────────────────────────────────────────────────────────
// No real substance — too short, all one repeated character, or no letters at
// all. Caught before the AI so it costs no question and no money.
export function isLowEffort(text) {
  const t = (text || "").trim();
  if (t.length < 3) return true;
  if (!/[a-z]/i.test(t)) return true;
  if (/^(.)\1+$/.test(t.toLowerCase().replace(/\s+/g, ""))) return true;
  return false;
}

// ─── User-facing copy ─────────────────────────────────────────────────────────
export const COPY = {
  greeting:
    "I'm the AI that knows Sam Erpik inside out — his work, his ventures, and " +
    "whether he's free to talk. What do you want to know?",
  nudge:
    "Worth knowing: I'm a real AI, wired up properly — rate-limited, cached, " +
    "logged. Five questions a day: enough for a real conversation, not enough " +
    "to be a nuisance. Sam thought of that.",
  examples: [
    "What has Sam built?",
    "What's Sam like to work with?",
    "Is Sam free for a project?",
  ],
  greetingReply:
    "Hey. Skip the small talk — you've got five good questions, let's not " +
    "spend one on hello. What do you want to know about Sam?",
  limitReached:
    "That's your five for today — and they were good ones. Want to keep going " +
    "properly? The contact form's right there, and Sam reads every message " +
    "himself.",
  lowEffort:
    "That's not much to go on. Ask a real question about Sam — his work, his " +
    "ventures, whether he's free — or tap one of the examples above.",
  limitBrief:
    "Still out of questions for today. Type 'hire sam' to reach him properly.",
};
